import Close from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect } from "react";

import ResponseModal from "../../../global/ResponseModal";
import { TextEditor } from "../../../ui library/components/text-editors/client-notes-text-editor";
import HNSColors from "../../../ui library/HNSColors";
import { useToastStore } from "../../Common/ToastProvider/store/useToastStore";
import CancelButton from "../Buttons/CancelButton";
import SubmitButton from "../Buttons/SubmitButton";

import { useEmailModalController } from "./hooks/useEmailModalController";

export default function EmailModal() {
  const {
    caseData,
    modalTitle,
    recipientOpts,
    formData,
    loading,
    isSubmitting,
    openSuccess,
    openError,
    errorMessage,
    isSendDisabled,
    setFormData,
    setOpenSuccess,
    setOpenError,
    handleSend,
    handleClose,
  } = useEmailModalController();
  const requestToast = useToastStore((state) => state.requestToast);
  useEffect(() => {
    if (openSuccess) {
      requestToast({
        message: "The recipients have been notified with a download link.",
        navigation: null
      });
      handleClose();
    }
  }, [openSuccess, requestToast, setOpenSuccess]);
  return (
    <>
      <Dialog open fullWidth maxWidth="md">
        <div className="p-6">
          <div className="flex w-full justify-end">
            <Close onClick={handleClose} className="cursor-pointer text-gray-400" />
          </div>

          <Typography fontSize={24} fontWeight={600} color={HNSColors.primary[500]}>
            {modalTitle}
          </Typography>

          <div className="mt-2 flex items-center gap-2 border-b border-gray-300 w-full">
            <Typography fontSize={12} color={HNSColors.neutrals[500]}>
              Send to:
            </Typography>
            <div className="flex-1">
            <Autocomplete
              multiple
              options={recipientOpts}
              value={formData.recipients}
              loading={loading.recipients}
              getOptionLabel={(o) => o?.email || ""}
              isOptionEqualToValue={(o, v) => o?.email === v?.email}
              onChange={(_, v) =>
                setFormData((p) => ({ ...p, recipients: v }))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option?.email || index}
                    label={option?.email}
                    size="small"
                    sx={{
                      color: HNSColors.primary[500],
                      backgroundColor: HNSColors.tertiary[950],
                      borderRadius: "8px",
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    border: "none",
                  }}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      "& fieldset": { border: "none" },
                    },
                    endAdornment: (
                      <>
                        {loading.recipients && (
                          <CircularProgress size={16} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} className="px-4 py-2">
                  <div className="text-sm">{option?.name} <span className="text-xs opacity-60"> - {option?.title}</span></div>
                  <div className="text-xs opacity-60">{option?.email}</div>
                </Box>
              )}
            />
            </div>
          </div>

          <div className="border-b border-gray-300 py-2">
            <span className="font-semibold text-[16px]" style={{color: HNSColors.neutrals[100]}}>{`${caseData?.name} documents`}</span>
          </div>

          <div className="mt-4">
            <TextEditor
              initialHeight={300}
              value={formData.emailBody}
              onChangeHandler={(v) =>
                setFormData((p) => ({ ...p, emailBody: v }))
              }
            />
          </div>

          <div className="mt-4 flex gap-4">
            <CancelButton handleCancel={handleClose} disabled={isSubmitting} />
            <SubmitButton
              disabled={isSendDisabled}
              handleSubmit={handleSend}
              buttonText={isSubmitting ? "Sending..." : modalTitle}
            />
          </div>
        </div>
      </Dialog>

      <ResponseModal
        title="Unable to Send Email"
        isError
        openBool={openError}
        setOpenBool={setOpenError}
        errorMessage={errorMessage}
      />
    </>
  );
}
